import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { NotificationsTabs } from '../../features/notifications/notifications-tabs';
import { TemplatesScreen } from '../../features/notifications/templates-screen';
import { NOTIFICATION_PERMISSIONS } from '../../features/notifications/types';
import { RequirePermission } from '../../lib/session/guards';

export const Route = createFileRoute('/notifications/templates')({
  /**
   * The open template lives in the query string, so "the 24-hour SMS reminder" is a link
   * somebody can paste into a message and closing the editor is the browser's Back.
   * Anything unrecognised falls back to the list rather than erroring — a stale id is a
   * link that has aged, not a failure.
   */
  validateSearch: (search: Record<string, unknown>): { template?: string } => {
    const id = search['template'];
    return typeof id === 'string' && id !== '' ? { template: id } : {};
  },
  component: TemplatesPage,
});

/**
 * `/notifications/templates` — the editor (MASTER_PLAN M9.8, FR-NOTIF-07).
 *
 * `notifications.read` opens it so a member of staff can read what the clinic sends;
 * publishing a version is `templates.manage`, which the screen renders read-only without
 * and the server refuses without.
 */
function TemplatesPage() {
  const { template } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <RequirePermission keys={[NOTIFICATION_PERMISSIONS.read]}>
      <TemplatesScreen
        selectedId={template ?? null}
        onSelect={(next) => {
          void navigate({
            to: '/notifications/templates',
            search: next === null ? {} : { template: next },
          });
        }}
        renderTabs={() => (
          <NotificationsTabs
            active="templates"
            renderLink={(to, children, props) => (
              <Link to={to} className={props.className} aria-current={props['aria-current']}>
                {children}
              </Link>
            )}
          />
        )}
      />
    </RequirePermission>
  );
}
